// src/pages/DashboardPage.tsx

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useI18n, useTask, useNotifications } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '@react/components';
import type { Notification } from '@core/types/notifications';

function statusVariant(s?: string) {
  if (!s) return 'secondary' as const;
  if (s.includes('BLOCKED')) return 'error' as const;
  if (s.includes('IN_PROGRESS') || s.includes('STARTED')) return 'primary' as const;
  if (s.includes('COMPLETED')) return 'success' as const;
  return 'secondary' as const;
}

function priorityVariant(p?: string) {
  if (!p) return 'secondary' as const;
  if (p.includes('CRITICAL')) return 'error' as const;
  if (p.includes('HIGH')) return 'warning' as const;
  return 'secondary' as const;
}

function isOpen(status?: string): boolean {
  if (!status) return true;
  return !status.includes('COMPLETED') && !status.includes('CANCELLED');
}

function isUrgent(priority?: string): boolean {
  if (!priority) return false;
  return priority.includes('CRITICAL') || priority.includes('HIGH');
}

function formatTime(ts: any): string {
  if (!ts) return '';
  const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString();
}

export const DashboardPage = observer(() => {
  const auth = useAuth();
  const { t } = useI18n();
  const tasks = useTask();
  const notifications = useNotifications();
  const [notificationItems, setNotificationItems] = useState<Notification[]>([]);

  useEffect(() => {
    tasks.ListMyTasks({
      pagination: { cursor: '', limit: 20, direction: 'forward' },
      sort: [],
    });
    if (auth.userId) {
      notifications.ListNotificationsByUser(auth.userId)
        .then(() => setNotificationItems([...notifications.items]))
        .catch(() => setNotificationItems([]));
    }
  }, []);

  const openTasks = tasks.items.filter(i => isOpen(i.status));
  const urgentTasks = openTasks.filter(i => isUrgent(i.priority));
  const unread = notificationItems.filter(n => !n.read_at);

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {t('dashboard.welcomeBack', { name: auth.user?.username || t('common.user') })}
        </h1>
        <p className="mt-1 text-muted-foreground">{t('dashboard.subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Link to="/tasks">
          <Card hoverable>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-muted-foreground">{t('dashboard.openTasks')}</p>
              <p className="mt-1 text-3xl font-bold text-foreground">
                {tasks.isLoading ? '\u2014' : openTasks.length}
              </p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/tasks">
          <Card hoverable>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-muted-foreground">{t('dashboard.urgentTasks')}</p>
              <div className="mt-1 flex items-baseline gap-2">
                <p className="text-3xl font-bold text-foreground">
                  {tasks.isLoading ? '\u2014' : urgentTasks.length}
                </p>
                {urgentTasks.length > 0 && (
                  <Badge variant="warning" size="sm">{t('dashboard.needsAttention')}</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/notifications">
          <Card hoverable>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-muted-foreground">{t('dashboard.unread')}</p>
              <p className="mt-1 text-3xl font-bold text-foreground">
                {notifications.isLoading ? '\u2014' : unread.length}
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Tasks + Notifications */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* My Tasks */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('dashboard.myTasks')}</CardTitle>
              <Link to="/tasks" className="text-sm text-primary hover:underline">
                {t('dashboard.viewAll')}
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {tasks.isLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : openTasks.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-muted-foreground">{t('dashboard.noTasks')}</p>
                <Link to="/tasks/new" className="mt-2 inline-block text-sm text-primary hover:underline">
                  {t('dashboard.createTask')}
                </Link>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {openTasks.slice(0, 8).map((item) => (
                  <li key={item.entity?.id}>
                    <Link
                      to={`/tasks/${item.entity?.id}`}
                      className="flex items-center justify-between gap-3 py-3 -mx-2 px-2 rounded hover:bg-muted/50 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <Badge variant={statusVariant(item.status)} size="sm">
                            {t(`dashboard.taskStatus.${statusKey(item.status)}`)}
                          </Badge>
                          {isUrgent(item.priority) && (
                            <Badge variant={priorityVariant(item.priority)} size="sm">
                              {t(`dashboard.taskPriority.${priorityKey(item.priority)}`)}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {item.due_date && (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatTime(item.due_date)}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('dashboard.notifications')}</CardTitle>
              <Link to="/notifications" className="text-sm text-primary hover:underline">
                {t('dashboard.viewAll')}
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {notifications.isLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : notificationItems.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('dashboard.noNotifications')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {notificationItems.slice(0, 6).map((n) => (
                  <li key={n.entity?.id} className={`py-3 ${!n.read_at ? '' : 'opacity-60'}`}>
                    <p className="text-sm font-medium text-foreground line-clamp-1">{n.subject}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.content}</p>
                    {n.created_at && (
                      <p className="text-xs text-muted-foreground/70 mt-1">{formatTime(n.created_at)}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Link to="/tasks/new" className="block">
          <Card hoverable>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <PlusIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">{t('dashboard.createTask')}</p>
                <p className="text-sm text-muted-foreground">{t('dashboard.createTaskDesc')}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/projects" className="block">
          <Card hoverable>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FolderIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">{t('dashboard.viewProjects')}</p>
                <p className="text-sm text-muted-foreground">{t('dashboard.viewProjectsDesc')}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/calendars" className="block">
          <Card hoverable>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <CalendarIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">{t('dashboard.viewCalendar')}</p>
                <p className="text-sm text-muted-foreground">{t('dashboard.viewCalendarDesc')}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
});

// Helpers for translation keys
function statusKey(s?: string): string {
  if (!s) return 'notStarted';
  if (s.includes('IN_PROGRESS')) return 'inProgress';
  if (s.includes('STARTED')) return 'started';
  if (s.includes('BLOCKED')) return 'blocked';
  if (s.includes('COMPLETED')) return 'completed';
  if (s.includes('CANCELLED')) return 'cancelled';
  return 'notStarted';
}

function priorityKey(p?: string): string {
  if (!p) return 'low';
  if (p.includes('CRITICAL')) return 'critical';
  if (p.includes('HIGH')) return 'high';
  if (p.includes('MEDIUM')) return 'medium';
  return 'low';
}

// Icons
function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

export default DashboardPage;
